import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import type {
  ISitemapNode,
  ISitemapNodeVariant,
  TSitemapTreeType,
} from '@informally/common/sitemaps';
import { TranslateService } from '@ngx-translate/core';
import { IBiLink } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/bi-link.interface';
import type { FromAPI, InterfaceConfig } from '@suvo/shared/interface-utils';
import { lastValueFrom } from 'rxjs';
import { ProductService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/product/services/product.service';

export interface GetChildNodesParams {
  treeType: TSitemapTreeType;

  /** Parent to get children for. Omit to get top-level nodes. */
  parentId?: string;

  /**
   * Whether the API should return a full subtree under `parentId`.
   *
   * Omit or set to false for legacy single-level behavior.
   */
  includeTree?: boolean;
}

export interface GetBranchParams {
  treeType: TSitemapTreeType;
  lang: string;
  pathFull: string;
  includeTree?: boolean;
}

export type SitemapNodeWithChildren = ISitemapNode<FromAPI> & {
  children?: SitemapNodeWithChildren[];
};

@Injectable({ providedIn: 'root' })
export class DynamicRouteService {
  private readonly httpClient = inject(HttpClient);
  private readonly translateService = inject(TranslateService);
  private readonly productService = inject(ProductService);

  private sanitizeParams(params?: object) {
    if (!params) {
      return undefined;
    }

    const filteredEntries = Object.entries(params).filter(([, value]) => value != null);
    return Object.fromEntries(filteredEntries);
  }

  protected get(route: string, params?: object) {
    return lastValueFrom(
      this.httpClient.get<SitemapNodeWithChildren[]>(`${environment.dataServiceApi}${route}`, {
        params: this.sanitizeParams(params),
      }),
    );
  }

  getChildNodes(params: GetChildNodesParams): Promise<SitemapNodeWithChildren[]> {
    return this.get('sitemap/child-nodes', params);
  }

  getBranch(params: GetBranchParams): Promise<SitemapNodeWithChildren[]> {
    return this.get('sitemap/branch', params);
  }

  /**
   * From the given sitemap node, returns the best available language variant.
   *
   * It will return the variant that satisfies the first of the following:
   *
   * 1. `lang` matches user's selected language.
   * 2. `lang` matches tenant's configured fallback language.
   * 3. `lang` is `en`.
   * 4. Failing all of the above, returns the first variant in the array.
   */
  resolveVariant<T extends InterfaceConfig>(node: ISitemapNode<T>): ISitemapNodeVariant<T> {
    if (!node?.variants) {
      return;
    }

    let fallbackVariant: ISitemapNodeVariant<T>;
    let enVariant: ISitemapNodeVariant<T>;

    for (const variant of node.variants) {
      if (variant.lang === this.translateService.currentLang) {
        return variant;
      } else if (!fallbackVariant && variant.lang === this.translateService.defaultLang) {
        fallbackVariant = variant;
        continue;
      } else if (!enVariant && variant.lang === 'en') {
        enVariant = variant;
      }
    }

    return fallbackVariant ?? enVariant ?? node.variants[0];
  }

  getRoutePrefix(treeType: TSitemapTreeType) {
    if (treeType === 'private') {
      return '/private';
    }
    if (treeType === 'public') {
      return `/${this.translateService.currentLang}`;
    }
  }

  isRouteUseable<T extends InterfaceConfig>(
    node: ISitemapNode<T>,
    variant = this.resolveVariant(node),
  ) {
    return Boolean(
      variant?.dashboardUuid || node.routeType === 'EMAIL_ALERTS' || node.routeType === 'EXPORT',
    );
  }

  shouldShowNodeInBreadcrumb<T extends InterfaceConfig>(node: ISitemapNode<T>) {
    return (
      node.routeType !== 'DASHBOARD_GROUP' &&
      (node.routeType === 'GROUP' || this.isRouteUseable(node))
    );
  }

  shouldIncludeNavNode<T extends InterfaceConfig>(node: ISitemapNode<T>, childLinksCount: number) {
    if (node.routeType === 'DASHBOARD_GROUP') {
      return false;
    }

    return this.isRouteUseable(node) || childLinksCount > 0;
  }

  async getLinks(params: GetChildNodesParams): Promise<IBiLink[]> {
    const routePrefix = this.getRoutePrefix(params.treeType);
    const links: IBiLink[] = [];
    const includeTree = params.includeTree ?? true;
    const nodes = await this.getChildNodes({
      treeType: params.treeType,
      parentId: params.parentId,
      includeTree,
    });

    // Get user's entitled datasource aliases (from ownedProductsList)
    const ownedProducts = this.productService.ownedProductsList.getValue() || [];
    const entitledAliases = new Set<string>();
    for (const product of ownedProducts) {
      for (const tag of product.tags || []) {
        if (tag.startsWith('DATASOURCE:')) {
          entitledAliases.add(tag.slice('DATASOURCE:'.length).toLowerCase());
        }
      }
    }

    const normalizeRequiredDataSourceAliases = (value: unknown) => {
      if (Array.isArray(value)) {
        return value.map((alias) => `${alias}`.trim().toLowerCase()).filter(Boolean);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((alias) => alias.trim().toLowerCase())
          .filter(Boolean);
      }

      return [];
    };

    for (const node of nodes) {
      // Filter by requiredDataSourceAliases if present
      const requiredDataSourceAliases = normalizeRequiredDataSourceAliases(
        node.requiredDataSourceAliases,
      );

      if (requiredDataSourceAliases.length) {
        const hasEntitlement = requiredDataSourceAliases.some((alias) =>
          entitledAliases.has(alias.toLowerCase()),
        );
        if (!hasEntitlement) {
          continue;
        }
      }

      const variant = this.resolveVariant(node);
      if (!variant) {
        continue;
      }

      // Subtree mode never issues per-node child API calls.
      // Legacy one-level mode keeps recursive calls.
      const childLinks = includeTree
        ? Array.isArray(node.children) && node.children.length
          ? await this.getLinksFromNodes(params.treeType, node.children, true)
          : []
        : await this.getLinks({
            treeType: params.treeType,
            parentId: node._id,
            includeTree: false,
          });

      if (node.routeType === 'DASHBOARD_GROUP') {
        links.push(...childLinks);
        continue;
      }

      if (!this.shouldIncludeNavNode(node, childLinks.length)) {
        continue;
      }

      const nodeIsUseable = this.isRouteUseable(node, variant);

      const link: IBiLink = {
        displayName: variant.name,
        route: nodeIsUseable ? routePrefix + variant.pathFull : undefined,
        displayIcon: node.navigationLinkIcon,
      };

      if (childLinks?.length) {
        link.subLinks = childLinks;
      }

      links.push(link);
    }

    return links;
  }

  private async getLinksFromNodes(
    treeType: TSitemapTreeType,
    nodes: SitemapNodeWithChildren[],
    treePayloadIsComplete = false,
  ): Promise<IBiLink[]> {
    const routePrefix = this.getRoutePrefix(treeType);
    const links: IBiLink[] = [];

    // Get user's entitled datasource aliases (from ownedProductsList)
    const ownedProducts = this.productService.ownedProductsList.getValue() || [];
    const entitledAliases = new Set<string>();
    for (const product of ownedProducts) {
      for (const tag of product.tags || []) {
        if (tag.startsWith('DATASOURCE:')) {
          entitledAliases.add(tag.slice('DATASOURCE:'.length).toLowerCase());
        }
      }
    }

    const normalizeRequiredDataSourceAliases = (value: unknown) => {
      if (Array.isArray(value)) {
        return value.map((alias) => `${alias}`.trim().toLowerCase()).filter(Boolean);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((alias) => alias.trim().toLowerCase())
          .filter(Boolean);
      }

      return [];
    };

    for (const node of nodes) {
      const requiredDataSourceAliases = normalizeRequiredDataSourceAliases(
        node.requiredDataSourceAliases,
      );

      if (requiredDataSourceAliases.length) {
        const hasEntitlement = requiredDataSourceAliases.some((alias) =>
          entitledAliases.has(alias.toLowerCase()),
        );
        if (!hasEntitlement) {
          continue;
        }
      }

      const variant = this.resolveVariant(node);
      if (!variant) {
        continue;
      }

      const childLinks = treePayloadIsComplete
        ? Array.isArray(node.children) && node.children.length
          ? await this.getLinksFromNodes(treeType, node.children, true)
          : []
        : node.children?.length
          ? await this.getLinksFromNodes(treeType, node.children)
          : await this.getLinks({ treeType, parentId: node._id, includeTree: false });

      if (node.routeType === 'DASHBOARD_GROUP') {
        links.push(...childLinks);
        continue;
      }

      if (!this.shouldIncludeNavNode(node, childLinks.length)) {
        continue;
      }

      const nodeIsUseable = this.isRouteUseable(node, variant);

      const link: IBiLink = {
        displayName: variant.name,
        route: nodeIsUseable ? routePrefix + variant.pathFull : undefined,
        displayIcon: node.navigationLinkIcon,
      };

      if (childLinks?.length) {
        link.subLinks = childLinks;
      }

      links.push(link);
    }

    return links;
  }
}
